import { useState } from "react";
import useVoiceOrbLevel from "./useVoiceOrbLevel";
import AssistantOrbVisual from "./AssistantOrbVisual";
import css from "./VoiceOrb.module.scss";

// Which analyser source the orb reacts to for the current conversation state.
const orbLevelMode = (voiceState) => {
  if (voiceState === "speaking") return "speaking";
  if (voiceState === "thinking") return "thinking";
  if (voiceState === "listening") return "listening";
  return "idle";
};

// The cloud theme bakes its blue/purple into a WebGL shader — there is no color
// prop and no per-state palette. Since it renders to a canvas, a CSS filter on
// the wrapper recolors the rendered pixels. Base cloud hue is ~235deg; +150deg
// lands on warm orange.
//
// Orange is reserved for `thinking`: the one state where the assistant is busy
// and the user is waiting on it. Blue everywhere else, so the color always
// means the same thing. (Tune band: 140deg reads redder, 160deg yellower.)
const THINKING_FILTER = "hue-rotate(150deg) saturate(1.25)";
const STATE_FILTERS = { thinking: THINKING_FILTER };
const NO_FILTER = "none";

// Animated orb for the voice dock. It owns its own audio-level state so only
// this subtree re-renders per frame — the bar's chrome (status, transcript,
// controls) stays put.
const VoiceOrb = ({ voiceState, micStream, ttsAnalyser, isMicMuted, size = 200 }) => {
  const [volume, setVolume] = useState(0);

  useVoiceOrbLevel({
    micStream,
    ttsAnalyser,
    mode: orbLevelMode(voiceState),
    onLevel: setVolume,
  });

  // Muting silences the microphone, not the orb. Zeroing the level in every
  // state would freeze the blob into a flat disc while the assistant thinks
  // and speaks, which is exactly when the user needs to see it is alive.
  const effectiveVolume =
    isMicMuted && voiceState === "listening" ? 0 : volume;

  return (
    <div
      className={css.orb}
      style={{
        "--orb-size": `${size}px`,
        filter: STATE_FILTERS[voiceState] || NO_FILTER,
        transition: "filter 400ms ease",
      }}
    >
      <AssistantOrbVisual
        size={size}
        state={voiceState}
        volume={effectiveVolume}
        ariaLabel="Voice assistant orb"
      />
    </div>
  );
};

export default VoiceOrb;
